import React, { useCallback } from 'react'
import { Skeleton } from 'antd'
import { Outlet, useLocation, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import type { Project } from 'types'
import { useHubProjectDetail } from '@features/projects/hub/hooks/useHubProjectDetail'
import { BreadcrumbHeader } from '@features/projects/hub/components/BreadcrumbHeader'
import { CreatedBanner } from '@features/projects/hub/components/CreatedBanner'
import { HubLifecycleStepper } from '@features/projects/hub/components/HubLifecycleStepper'
import { BackToProjectsLink } from '@features/projects/hub/components/BackToProjectsLink'
import {
  ErrorBlock,
  OpenButton,
  PageWrapper,
} from '@features/projects/hub/list/styles'
import { OPTIONAL_DOCS_QUERY_KEY } from '@features/projects/hub/components/OptionalDocumentSection'
import { DOCUMENTS_VIEW_QUERY_KEY } from 'src/features/documents/hooks/useDocumentsView'
import type { SynthesisOutletContext } from './ProjectSynthesisLayout'

// ---------------------------------------------------------------------------
// SalesmanSynthesisLayout — the salesman half of the shared synthesis route.
//
// `ProjectSynthesisLayout` switches to this component for the project-hub roles
// (SALESMAN + SOCIETY). It renders the salesman's own chrome — the breadcrumb
// header and the lifecycle stepper — and provides the SAME `SynthesisOutletContext`
// shape ({ project, invalidateProject }) the architect layout does, so the
// dispatcher (`synthesis/index.tsx`), `useSynthesisAction`, and the numbered step
// pages work for both roles unchanged.
//
// It deliberately does NOT render any architect chrome (planning-file card,
// "Commercial" card, Fermer / Supprimer le projet actions) and sources its data
// from `useHubProjectDetail` — the salesman-permission-safe projection — rather
// than the architect layout's query (which selects `salesman.account.email`).
// ---------------------------------------------------------------------------

export const SalesmanSynthesisLayout: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = id ? Number(id) : undefined
  // Set by the creation wizard on the navigation that lands here (#670 §9).
  // Router state, not a query param: it survives neither a reload nor a shared
  // link, which is exactly right for a one-time "it worked" acknowledgement.
  const { state } = useLocation()
  const justCreated =
    (state as { justCreatedProjectId?: number } | null)
      ?.justCreatedProjectId === projectId && projectId !== undefined
  const queryClient = useQueryClient()

  const { project, isLoading, isError, refetch } = useHubProjectDetail(projectId)

  // Force-refresh the whole salesman view after a state transition: re-run the
  // detail query (so the dispatcher advances to the new step's page) and
  // invalidate every React-Query cache the step body reads. Mirrors the
  // fan-out the monolithic `ProjectHub` used to perform inline.
  const invalidateProject = useCallback(() => {
    refetch()
    if (projectId === undefined) return
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    queryClient.invalidateQueries({ queryKey: ['project-status', projectId] })
    queryClient.invalidateQueries({
      queryKey: DOCUMENTS_VIEW_QUERY_KEY(projectId),
    })
    queryClient.invalidateQueries({
      queryKey: OPTIONAL_DOCS_QUERY_KEY(projectId),
    })
  }, [refetch, projectId, queryClient])

  if (isLoading) {
    return (
      <PageWrapper data-cy="projects/hub">
        <BackToProjectsLink />
        <Skeleton active paragraph={{ rows: 8 }} />
      </PageWrapper>
    )
  }

  if (isError || !project) {
    return (
      <PageWrapper data-cy="projects/hub">
        <BackToProjectsLink />
        <ErrorBlock>
          <span>Une erreur est survenue lors du chargement du projet.</span>
          <OpenButton
            $variant="ghost"
            onClick={() => refetch()}
            data-cy="action--retry"
          >
            Réessayer
          </OpenButton>
        </ErrorBlock>
      </PageWrapper>
    )
  }

  const context: SynthesisOutletContext = {
    project: project as Project,
    invalidateProject,
  }

  return (
    <PageWrapper data-cy="projects/hub">
      {justCreated && projectId !== undefined && (
        <CreatedBanner
          reference={`AZ-${projectId.toString().padStart(4, '0')}`}
        />
      )}
      <BreadcrumbHeader project={project as never}>
        <HubLifecycleStepper
          currentStep={project.step as never}
          depositMode={
            (project as { deposit_mode?: string | null }).deposit_mode
          }
        />
      </BreadcrumbHeader>
      <Outlet context={context} />
    </PageWrapper>
  )
}

export default SalesmanSynthesisLayout
